import React, { ChangeEvent } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from '@tanstack/react-form';
import { useAuth } from '../../../../contexts/AuthContext';
import { Unauthorized } from '../../../../components/Unauthorized';
import { Chart } from '../../../../types/Chart/Chart';
import useChart from '../../../../hooks/useChart';
import { PatientDoc } from '../../../../types/Patient/PatientDoc';
import usePatientDoc from '../../../../hooks/usePatientDoc';
import { Product } from '../../../../types/Products/Products';
import useProducts from '../../../../hooks/useProducts';
import { NavForward } from '../../../../components/buttons/NavForward';
import { FormInput } from '../../../../components/forms/FormInput';
import { Button } from '../../../../components/buttons/Button';
import { useUpdateChartSection } from '../../../../hooks/useUpdateChartSection';
import { IsSigningObject } from '../../../../types/Signatures/IsSigningObject';
import { SignatureArea } from '../../../../components/SignatureArea';
import { FileTypes } from '../../../../types/Files/FileTypes';
import { getNextPage } from '../../../../data/navigation/getNextPage';
import { markPreviousPageCompleted } from '../../../../data/navigation/utilities/markPreviousPageCompleted';
import { Heading } from '../../../../components/Heading';
import * as Sentry from '@sentry/browser';
import Checkbox from '../../../../components/forms/Checkbox';

export const Route = createFileRoute('/_authenticated/$patientId/payment/medicareABN')({
  component: MedicareABNPage,
});

function MedicareABNPage() {
  const { patientId } = Route.useParams<{ patientId: string }>();
  const { currentUser } = useAuth();
  const chartQuery = useChart(patientId);
  const { isFetched: chartIsFetched } = chartQuery;
  const chart = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const productsDocQuery = useProducts();
  const productsDocData = productsDocQuery.data as Product[];
  const { mutate } = useUpdateChartSection();
  const [chosenOption, setChosenOption] = React.useState('');
  const [selectedProduct, setSelectedProduct] = React.useState<Product>();
  const [nextPageURL, setNextPageURL] = React.useState<string | undefined>(undefined);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [isSigning, setIsSigning] = React.useState({
    patientFinal: false,
    witness1: false,
    witness2: false,
    hipaa: false,
    consent: false,
    forms: false,
    abnMedicare: false,
    abn: false,
    aob1: false,
    aob2: false,
  } as IsSigningObject);

  // SET SELECTED PRODUCT
  React.useEffect(() => {
    if (productsDocData && chart) {
      let productFullName = '';
      switch (chart?.PRODUCT_INFO?.selectedProduct) {
        case 'essential':
          productFullName = 'Essential Package';
          break;
        case 'vital':
          productFullName = 'Vital Package';
          break;
        case 'vip':
          productFullName = 'Physician Guided Package';
          break;
        default:
          break;
      }
      setSelectedProduct(productsDocData.find((product) => product.name === productFullName));
    }
  }, [productsDocData, chart]);

  const medicareOptions = {
    optionOne:
      'OPTION 1. I will be asked to pay now, but I also want my insurance billed for an official decision on payment. I understand if my insurance doesn’t pay, I am responsible for payment, but I can appeal to my insurance carrier. If my insurance does pay, you will refund any payments I made to you, less co-pays or deductibles.',
    optionTwo:
      'OPTION 2. Do not bill Medicare. You may ask to be paid now as I am responsible for payment. I cannot appeal if Medicare is not billed.',
    optionThree:
      "OPTION 3. I don't want a MIDEO. I understand with this choice I am not responsible for payment, and I cannot appeal to see if Medicare would pay.",
  };

  // SET STATE TO TRIGGER EFFECT
  const handleCheckboxClick = (e: ChangeEvent<HTMLInputElement>) => {
    switch (e.target.name) {
      case 'optionOne':
        setChosenOption(medicareOptions.optionOne);
        break;
      case 'optionTwo':
        setChosenOption(medicareOptions.optionTwo);
        break;
      case 'optionThree':
        setChosenOption(medicareOptions.optionThree);
        break;
      default:
        break;
    }
  };

  // WRITE FORM DATA TO FIRESTORE
  const handleMedicareABNInfoUpdate = () => {
    mutate(
      {
        patientId: patientId,
        docData: {
          ABN_INFO: {
            additionalInfo: medicareABNForm.state.values.additionalInfo,
            medicareABNChoice: chosenOption,
            nonMedicareABNChoice: chart?.ABN_INFO?.nonMedicareABNChoice ? chart.ABN_INFO.nonMedicareABNChoice : '',
            returning: true,
            signature1DataURI: chart?.ABN_INFO ? chart.ABN_INFO.signature1DataURI : '',
          },
        },
      },
      {
        onSuccess: () => {
          // alert('Successfully updated document');
          setIsSubmitted(true);
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // INITIALIZE EMPTY FIRESTORE DOC IF IT DOESN'T EXIST
  React.useEffect(() => {
    const docRef = chart?.ABN_INFO;
    if (chart && !docRef) {
      handleMedicareABNInfoUpdate();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chart]);

  // INITIALIZE FORM
  const medicareABNForm = useForm({
    defaultValues: {
      additionalInfo: chart?.ABN_INFO?.additionalInfo ? chart.ABN_INFO.additionalInfo : '',
      medicareABNChoice: chart?.ABN_INFO?.medicareABNChoice ? chart.ABN_INFO.medicareABNChoice : '',
      returning: chart?.ABN_INFO?.returning ? chart.ABN_INFO.returning : false,
      signature1DataURI: chart?.ABN_INFO ? chart.ABN_INFO.signature1DataURI : '',
    },
    onSubmit: async () => {
      await handleMedicareABNInfoUpdate();
    },
  });

  // MARK PREVIOUS PAGE AS COMPLETED
  React.useEffect(() => {
    if (patientInfo && patientInfo.navObj) {
      markPreviousPageCompleted({ currentPageName: 'medicareABN', patientInfo, value: true });
    }
  }, [patientInfo]);

  // GET NEXT PAGE URL
  React.useEffect(() => {
    if (chart?.PAYMENT_INFO && patientInfo.navObj && !nextPageURL) {
      (async () => {
        const awaitedUrl = await getNextPage({
          currentPage: 'medicareABN',
          chart: chart,
          patientInfo: patientInfo,
        });
        setNextPageURL(awaitedUrl);
      })();
    }
  }, [chartIsFetched, patientInfo, nextPageURL]);

  const nextPage = `../../../${currentUser?.uid}/payment/aob`;

  if (chartQuery.isLoading || !chart) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && chart?.PATIENT_INFO_1) {
    return (
      <>
        {/* <Header patientId={patientId} /> */}
        <div id="medicare-abn-page" className="flex flex-col items-center justify-center">
          <Heading id="medicare-abn-page-header" textContent="Advance Beneficiary Notice of Non-Coverage (ABN)" />
          <div id="medicare-abn-page-main" className="flex w-5/6 flex-row justify-around">
            <div className="flex flex-col items-start">
              <div className="font-bold">
                <p className="text-2xl">MIDEO ID Card</p>
                <p className="text-2xl">Video Advance Directive</p>
              </div>
              <p id="medicare-abn-description-text" className="text-xl">
                Medicare may not pay for everything. To submit your payment, please enter your information and click on
                the &quot;Submit&quot; button below. The total cost for the process and the physical MIDEO ID is $
                {selectedProduct?.oneTimePrice}.
              </p>
              <div className="h-12"></div>
              <p id="medicare-abn-insurance-disclaimer" className="text-xl">
                If you choose Option 1 or 2, we may help you to use any other insurance that you might have, but
                Medicare cannot require us to do so.
              </p>
              <div id="option-checkbox-group" className="flex flex-col">
                {/* <div id="option-one-container" className="my-2"> */}
                <Checkbox
                  id="option-one"
                  label={medicareOptions.optionOne}
                  name="optionOne"
                  onChange={(e) => {
                    handleCheckboxClick(e);
                  }}
                  checked={chosenOption === medicareOptions.optionOne}
                />
                <Checkbox
                  id="option-two"
                  label={medicareOptions.optionTwo}
                  name="optionTwo"
                  onChange={(e) => {
                    handleCheckboxClick(e);
                  }}
                  checked={chosenOption === medicareOptions.optionTwo}
                />
                <Checkbox
                  id="option-three"
                  label={medicareOptions.optionThree}
                  name="optionThree"
                  onChange={(e) => {
                    handleCheckboxClick(e);
                  }}
                  checked={chosenOption === medicareOptions.optionThree}
                />
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  medicareABNForm.handleSubmit();
                }}
              >
                <div className="my-4 flex flex-row">
                  <medicareABNForm.Field
                    name="additionalInfo"
                    children={(field) => (
                      <>
                        <FormInput
                          label="Additional Info: "
                          id="additional-info"
                          name={field.name}
                          value={field.state.value}
                          handleBlur={field.handleBlur}
                          handleChange={(e) => field.handleChange(e.target.value)}
                          required={false}
                        />
                      </>
                    )}
                  />
                </div>
              </form>
              <SignatureArea
                elementId={'medicare-abn-sig'}
                type={FileTypes.medicareABNSig}
                data={chart}
                patientId={patientId}
                isSigning={isSigning}
                cloudStoragePath={`/user/${patientId}/signatures/ABN_INFO_ABNSignature`}
                setIsSigning={setIsSigning}
                docToUpdate={'ABN_INFO'}
                sigName="abnMedicare"
                canComeFromClone={true}
              />
              <div className="flex w-full flex-col items-center">
                <Button
                  id="submit-medicare-abn-info"
                  type="submit"
                  textContent="Submit"
                  onClick={handleMedicareABNInfoUpdate}
                />
              </div>
            </div>
          </div>
          <div id="nav" className="flex flex-row">
            {nextPage && isSubmitted && (
              <NavForward
                to={nextPage}
                disabled={!chart?.ABN_INFO?.signature1DataURI || !chart?.ABN_INFO?.medicareABNChoice}
              />
            )}
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
